<template>
  <v-container>
    <v-row justify="center" class="title primary--text"><h1>Encoding combustion experimental data</h1></v-row>
    <v-row class="my-5">
      <h1 id="v3-introduction">Introduction</h1>
    </v-row>
    <v-row>
      <v-col>
        <p>
          In our <router-link to="/videos/video1">previous video</router-link>,
          I have discussed briefly that XML format data files can be used for the storage of the complete description of a combustion experiment and the experimental results.
          It is very useful if such datafiles are accompanied with a code that is able to create, read and process the data files. In our case this is the Optima++ program.
        </p>
        <p>
          Our group started to use the PrIMe Kinetics Dataformat, which is an XML format for encoding combustion experimental data.
          Having worked for several years with the PrIMe format, we decided to use an extended,
          not backward compatible version of it that we call ReSpecTh Kinetics Data format or RKD format.
          The latest version is 2.2 and the the detailed format specification is available here <citation id="RespecthSpec" :references="references" />.
        </p>
      </v-col>
    </v-row>
    <v-row class="my-5">
     <h1 id="v3-rkd">ReSpecTh Kinetics Data format</h1>
    </v-row>
    <v-row>
      <v-col>
        <p>
          XML is the acronym of Extensible Markup Language.
          It is a markup language that defines a set of rules for encoding documents in a format that is both human-readable and machine-readable.
          Here is an example of an RKD data file:
        </p>
        <highlight-code lang="xml" class="code-block mb-6" :code="rkdExample" />
        <p>
          There are three different types of RKD files, which are defined with the first tag (the root element) of the file.
          It can be <highlight-code lang="xml" class="code-block" inline code="<experiment>" /> corresponding to an indirect measurement,
          <highlight-code lang="xml" class="code-block" inline code="<kdetermination>" /> corresponding to an experimental or theoretical direct determination of a rate coefficient,
          or <highlight-code lang="xml" class="code-block" inline code="<xmlinfo>" /> containing additional information for the description of an experiment.
        </p>
        <p>
          An RKD file consists of four main parts.
          First, the facts about the XML file are given, which include the ID of the person or research group which created the XML file
          (<highlight-code lang="xml" class="code-block" inline code="<fileAuthor>" />),
          the unique DOI of the XML file (<highlight-code lang="xml" class="code-block" inline code="<fileDOI>" />),
          the version of the data file (<highlight-code lang="xml" class="code-block" inline code="<fileVersion>" />) and
          the version of the RKD format (<highlight-code lang="xml" class="code-block" inline code="<ReSpecThVersion>" />).
          If an error is found in a published XML file, then the <highlight-code lang="xml" class="code-block" inline code="<fileVersion>" /> number is increased and the old file is
          replaced by the new one in the ReSpecTh database. In the past, the RKD format was revised in several steps and the newer versions allow inserting additional
          types of information to the datafile. The RKD files are all backward compatible.
          If an RKD file was modified in such a way that the experimental data in the XML file remained identical, but the RKD format was upgraded by inserting additional information
          (like more detailed bibliographical data), then the <highlight-code lang="xml" class="code-block" inline code="<fileVersion>" /> remain identical
          and the <highlight-code lang="xml" class="code-block" inline code="<ReSpecThVersion>" /> is corrected.
        </p>
        <p>
          The second block contains information about the source of the data. If the origin of the data is a journal article,
          then this block contains the DOI number of the article (<highlight-code lang="xml" class="code-block" inline code="<referenceDOI>" />),
          the bibliographical details of the article and the exact location of the experimental data within the article.
        </p>
        <p>
          The third block is the <highlight-code lang="xml" class="code-block" inline code="<commonProperty>" /> part which contains the constant or initial experimental conditions
          and other common information valid for each data point.
          The fourth block is the <highlight-code lang="xml" class="code-block" inline code="<dataGroup>" /> part which contains the actual data points of the experimental
          or theoretical results.
        </p>
        <p>
          Each RKD format XML file contains a dataset.
          A dataset includes those data points that were measured on the same apparatus at the same time at similar conditions except for one that was systematically changed.
          The text of the article has to be used to understand the experimental method, the initial temperature and pressure, the initial concentrations and so on.
          At the measurement of ignition delay times in shock tubes, the experimental definition of the ignition delay time is usually based on the measurement of pressure change
          or the concentration profile of some species. Exactly the same definition has to be used at the simulations, if we want to compare correctly the experimental data
          and simulation results.
        </p>
        <p>
          I agree that an RKD format file looks fairly complicated at first sight, but fortunately you do not have to create such files manually.
          In the coming minutes I will show how such a data file can be created easily using the graphical user interface of our Optima++ code.
        </p>
        <p>
          The data themselves may come from your own experiments or from published papers.
          The procedure is almost identical in the two cases.
          In the articles some experimental data are given in the Supplementary in the form of Excel tables or text files,
          while other measurement data can be read from the figures of the article. In the latter case you have to digitize the content of the figures first.
        </p>
      </v-col>
    </v-row>
    <v-row class="my-5">
      <h1 id="v3-xml-encoding">Creating RKD format xml file using Optima++</h1>
    </v-row>
    <v-row>
      <v-col>
        <p>
          Let us assume that you want to encode a data set from an article of Ultan Burke and his coworkers, published in journal Combustion and Flame in 2016 <citation id="Burke2016" :references="references"/>.
        </p>
        <p>
          We will encode the first dataset reported in the xlsx file given as a <a target="_blank" href="https://ars.els-cdn.com/content/image/1-s2.0-S0010218015003958-mmc3.xlsx">supplementary material</a>.
        </p>
        <p>
          If I click on the <code>create RKD format XML data file</code> button in Optima++ GUI,
          then a wizard window will show up, where I can select a template to start with.
          These templates are useful if you are encoding your own results,
          because you can create a template containing your not changing data and reuse it during the preparation of multiple xmls.
          In the next page I can select the experiment type. I will encode now "ignition delay measurement" data,
          so I will select the first option and click on the finish button.
        </p>
        <image-viewer :src="require('@/assets/v3-wizard.png')" :small-width="500" />
        <p>
          In the next window, I can edit my new xml file.
          As you can see, here we have four tabs corresponding to the four main parts of the xml file.
        </p>
       <image-viewer :src="require('@/assets/v3-xmleditor.png')" :small-width="500" />
        <p>
          You can edit any field by double clicking on it.
          In the <code>File info</code> section, I update the <code>File DOI</code> field.
        </p>
        <image-viewer :src="require('@/assets/v3-fileDOI.png')" :small-width="500" />
        <p>
          On the next tab, we can edit the reference of the experimental data.
          If you download the <a href="/tutorials/Burke2016_bibliography.bib">BibTeX file</a> of the selected article from the web page of the journal,
          you can simply drag and drop this file into the Reference section and the relevant fields will be automatically imported from the BibTeX file.
        </p>
        <image-viewer :src="require('@/assets/v3-bibtex-drag.png')" :small-width="500" />
        <image-viewer :src="require('@/assets/v3-bibtex-imported.png')" :small-width="500" />
        <p>
          Alternatively, you can click on the <code>Import bibliography data</code> button on the toolbar. This will open a file dialog window where you can locate the BibTeX file.
        </p>
        <image-viewer :src="require('@/assets/v3-toolbar.png')" :small-width="203" />
        <p>
          Now I add a new <code>location</code> field, which contains the location of the data to be encoded within the article.
          In the right click menu I select the <code>Add new field</code> option and in the combo box I chose the <code>location</code> field.
          In the editor window, I can type in the value of the location field.
        </p>
       <image-viewer :src="require('@/assets/v3-location.png')" :small-width="500" />
        <p>
          In the next section, we can set up the common experimental conditions.
          The initial composition will be inserted here.
          Click on the <code>Add new property</code> item in the right click context menu and select <code>composition</code> from the drop down list.
          In the property editor window, we can fill the relevant fields based on the data reported in the supplementary material.
          If we have typed the name of the species, all other fields are filled in automatically by clicking on the small magic button next to the <code>Name</code> field.
        </p>
        <image-viewer :src="require('@/assets/v3-property-editor.png')" :small-width="500" />
        <p>
          If we repeat the same procedure for the oxygen and nitrogen data, the xml editor will look like this:
        </p>
        <image-viewer :src="require('@/assets/v3-comm-prop.png')" :small-width="500" />
        <p>
          In the last section, we need to provide the varied data of the experiment.
          First, let us define which properties were measured.
          So right click, <code>add new property</code> and select temperature similarly as we did in the common properties section.
          It is up to you in which order you define the properties. The only difference is that we have to define a unique ID for each property
          in the property editor window. I will use ID's <code>x1</code>, <code>x2</code> and so on.
        </p>
        <image-viewer :src="require('@/assets/v3-var-prop.png')" :small-width="500" />
        <p>
          Now we can insert the measured data in the bottom table. So I copy the corresponding columns from the supplementary material to the system clipboard.
          It is not a problem if the columns of the data are not in the same order as listed in the xml editor
          or if there are extra columns. If we select the <code>Paste data from clipboard</code> option from the right click menu of the xml editor window, the
          <code>Insert data from clipboard</code> dialog window will pop up.
           In this window, you can set up how the data in the clipboard are utilized, for example you can overwrite the existing content or append rows to the end of the table.
        </p>
        <image-viewer :src="require('@/assets/v3-var-prop-copy.png')" :small-width="500" />
        <p>
          In the middle section, I can reorder the columns.
          The first column is for the pressure data,
          the second is for the temperature.
          The third column contains 1000/T data, we will not use it,
          so I select the <code>not used</code> option.
          The last column is the ignition delay.
          If everything is fine, I click on the OK button to accept it.
        </p>
        <image-viewer :src="require('@/assets/v3-var-prop-paste.png')" :small-width="500" />
        <p>

          Now our xml file is ready and we can save it by clicking on the floppy disk icon in the toolbar at the top of the window or
          by using <code class="shortcut">Ctrl + S</code> keyboard shortcut.
        </p>
        <image-viewer :src="require('@/assets/v3-var-prop-final.png')" :small-width="500" />
      </v-col>
    </v-row>
    <v-row class="my-5">
      <h1 id="v3-summary">Summary</h1>
    </v-row>
    <v-row>
      <v-col>
        <p>
          I have shown how shock tube data published in an article can be converted to an RKD datafile.
          There is a series of well-established experimental methods for the investigation of the chemistry of combustion systems.
          These are laminar burning velocity measurements, ignition delay times measured in shock tubes or rapid compression machines,
          concentration measurements related to reactors like shock tubes, jet stirred reactors, flow reactors and flat flames.
          All these experimental data can be easily encoded to RKD format using the Optima++ program in a similar way.
        </p>
        <p>
          Some final comments. Our group has created several thousand RKD datafiles and published them in the <a target="_blank" href="http://respecth.chem.elte.hu/respecth/">ReSpecTh information system</a>.
          These datafiles provide an almost complete coverage of the experimental investigation of the combustion of hydrogen, syngas, methane, methanol, ethanol, butanol and ammonia.
          Also, many experimental data are available for combustion systems in the presence of nitrogen oxides.
          We suggest to look for a given set of experimental data in ReSpecTh before you start encoding an RKD file from a journal article.
        </p>
        <p>
          Experimental data published in a journal article are dormant and cannot be used directly.
          Encoding published data in RKD-format files allow an easy and efficient utilization of their information content.
          The encoded RKD files can be preserved in your archive only or you may publish them in your web site.
          However, we are also ready to host your data in <a target="_blank" href="http://respecth.chem.elte.hu/respecth/">respect.hu</a>, with a full credit to the person who created the RKD data file.
        </p>
        <p>
          Usually the experimental data measured in a laboratory are stored in a paper or electronic logbook.
          The student who did the measurements graduate. The unpublished data and the fine details of the published data may become unavailable in a few years.
          The typical reasons are that the electronic logbook system is replaced by a new, not compatible one, and the paper logbook does not contain all details.
          We suggest encoding of all new experimental data in RKD format.
          This is a both human and machine readable ASCII data format which allows a permanent storage.
        </p>
        <p>
          In our <router-link to="/videos/video1">previous video</router-link>, I have shown that if an RKD file is available for the description of a combustion experiment,
          then the experimental data and the simulation results can be compared easily using program Optima++.
        </p>
      </v-col>
    </v-row>

    <v-row class="my-5">
      <h1 id="v3-references">References</h1>
    </v-row>
    <v-row>
      <v-col>
        <references :references="references"></references>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import Citation from '@/components/Citation'
import References from '@/components/References'
import ImageViewer from '@/components/ImageViewer'
import xml from '@/assets/x10002069.txt'
export default {
  name: "Video3",
  components: {Citation, References, ImageViewer},
  data: () => {
    return {
      references: [
        {
          id: 'RespecthSpec',
          title: 'ReSpecTh Kinetics Data Format Specification v2.2',
          author: 'Tamás Varga and Carsten Olm and Ágota Busai and István Gy. Zsély',
          year: '2019',
          from: 'http://www.respecth.hu',
          download: '/tutorials/ReSpecTh_Kinetics_Data_Format_Specification_v2.2.pdf',
          downloadName: 'ReSpecTh Kinetics Data Format Specification v2.2',
          icon: 'mdi-file-pdf'
        },
        {
          id: 'Burke2016',
          title: 'A detailed chemical kinetic modeling, ignition delay time and jet-stirred reactor study of methanol oxidation',
          journal: 'Combustion and Flame',
          volume: '165',
          pages: '125 - 136',
          year: '2016',
          doi: 'https://doi.org/10.1016/j.combustflame.2015.11.004',
          author: 'Ultan Burke and Wayne K. Metcalfe and Sinead M. Burke and K. Alexander Heufer and Philippe Dagaut and Henry J. Curran'
        }
      ],
      rkdExample: xml
    }
  },
  methods: {
    bookMarks ()  {
      return [
        {
          id: '#v3-introduction',
          title: 'Introduction'
        },
        {
          id: '#v3-rkd',
          title: 'RKD format'
        },
        {
          id: '#v3-xml-encoding',
          title: 'Encoding experiments'
        },
        {
          id: '#v3-references',
          title: 'References'
        }
      ]
    }
  }
}
</script>

<style scoped>

</style>